import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getRoutePath } from "../../../components/routes/routes";
import { ApiUrl } from "../../constants";
import { useLocationState } from "../../utils";
import { PaginationResults, QueryUrl } from "../common.api";
import { prepareHeaders } from "../user/user.api";
import {
  PartageProjet,
  Projet,
  ProjetFormData,
  RetraitFormData,
} from "./projet.type";

type ProjectOnlineQuery = {
  page?: number;
  name__icontains?: string;
  category_id?: number;
  statut?: "offline" | "termine" | "online";
};

type ProjectByUserQuery = {
  page?: number;
  id: number;
};
const ProjetApi = createApi({
  reducerPath: "listProjet",
  baseQuery: fetchBaseQuery({
    baseUrl: `${ApiUrl}/api`,
    prepareHeaders,
  }),
  tagTypes: ["listProjet", "projet", "projet_user", "partage"],
  endpoints: (build) => ({
    projetsOnline: build.query<PaginationResults<Projet>, ProjectOnlineQuery>({
      query: (query) => QueryUrl(`/projetbyfilter/`, query),
      providesTags: ["listProjet"],
    }),
    projets: build.query<
      PaginationResults<Projet>,
      { page: number; wordprojet: string; wordporteur: string }
    >({
      query: (query) => QueryUrl(`/projetbyfilteradmin/`, query),
      providesTags: ["listProjet"],
    }),
    projetsByUser: build.query<PaginationResults<Projet>, ProjectByUserQuery>({
      query: ({ id, ...query }) => QueryUrl(`/projetbyuser/${id}/`, query),
      providesTags: ["projet_user"],
    }),
    projetBySlug: build.query<Projet, string>({
      query: (slug) => `/projetbyvisitor_slug/${slug}/`,
      providesTags: (r) => ["projet"],
    }),
    // Un projet prive s'ouvre par son lien secret, pas par son slug.
    projetByLien: build.query<Projet, string>({
      query: (lien) => `/projet-lien/${lien}/`,
    }),
    partage: build.query<PartageProjet, number>({
      query: (id) => `/projet/${id}/partage/`,
      providesTags: ["partage"],
    }),
    modifierPartage: build.mutation<
      PartageProjet,
      { id: number; visibilite: "public" | "prive" }
    >({
      query: ({ id, ...body }) => ({
        url: `/projet/${id}/partage/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["partage", "projet_user", "listProjet", "projet"],
    }),
    addProjet: build.mutation<Projet, { id?: number; data: ProjetFormData }>({
      query: ({ id, data }) => ({
        url: id ? `/projet/${id}/` : `/projet/`,
        method: id ? "PUT" : "POST",
        body: data,
      }),
      invalidatesTags: ["listProjet", "projet_user"],
    }),
    deleteProjet: build.mutation<Projet, number>({
      query: (id) => ({
        url: `/projet/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["listProjet", "projet_user"],
    }),
    addRetrait: build.mutation<Projet, RetraitFormData>({
      query: (data) => ({
        url: `/retrait/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["listProjet", "projet_user"],
    }),
  }),
});

export const {
  useAddProjetMutation,
  useProjetBySlugQuery,
  useLazyProjetBySlugQuery,
  useLazyProjetByLienQuery,
  usePartageQuery,
  useModifierPartageMutation,
  useProjetsOnlineQuery,
  useProjetsByUserQuery,
  useProjetsQuery,
  useDeleteProjetMutation,
  useAddRetraitMutation,
} = ProjetApi;

export default ProjetApi;

export function useProjetFromLocation(): [
  Projet,
  boolean,
  string,
  (slug: string) => any
] {
  const itemState = useLocationState<Projet | undefined>(undefined);
  const [item, setItem] = useState(itemState);
  const { slug, lien } = useParams<{ slug: string; lien: string }>();
  const [findBySlug, parSlug] = useLazyProjetBySlugQuery();
  const [findByLien, parLien] = useLazyProjetByLienQuery();
  // /app/c/<lien> : projet prive ouvert par son lien secret.
  const requete = lien ? parLien : parSlug;
  const { data, isError, isLoading } = requete;
  const navigate = useNavigate();

  useEffect(() => {
    if (!itemState) {
      if (lien) findByLien(lien);
      else findBySlug(slug as any);
    }
  }, [itemState, slug, lien]);
  useEffect(() => {
    if (isError) {
      navigate(getRoutePath("home"));
    }
  }, [isError]);
  useEffect(() => {
    if (data) {
      setItem(data);
    }
  }, [data]);

  return [item as Projet, isLoading, slug as string, findBySlug as any];
}
